import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, ChevronDown, Phone, Mail, Home, HelpCircle, Building2,
  BadgeCheck, TrendingUp, MessageSquare, FileText, Shield,
  ArrowRight, Sparkles, Scale, Globe2, MapPin, Layers, Factory, Tag,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { useAppSelector } from '../hooks/redux';
import { sharedCompanyTypes, supportedCountries, switchCountryAndRefresh } from '../content/siteContent';

type MegaItem = {
  label: string;
  desc: string;
  href: string;
  icon: React.ElementType;
  color: string;
};

type MegaMenu = {
  heading: string;
  items: MegaItem[];
  secondary?: { heading: string; items: MegaItem[] };
  featured?: { label: string; href: string; tag: string; desc: string };
};

const companyTypeIcons = [Building2, Layers, Factory, Globe2, Sparkles, Scale, BadgeCheck, Building2];
const companyTypeColors = ['#C2185B', '#E91E63', '#7B1FA2', '#1B8A5A', '#FF7043', '#C2185B', '#7B1FA2', '#E91E63'];

const megaMenus: Record<string, MegaMenu> = {
  Solutions: {
    heading: 'What you get',
    items: [
      { label: 'Credit Rating', desc: 'First step of every raise', href: '/credit-rating', icon: BadgeCheck, color: '#C2185B' },
      { label: 'Fundraising', desc: 'Shorter path to capital', href: '/fundraising', icon: TrendingUp, color: '#E91E63' },
      { label: 'How it works', desc: 'Rate → Raise in one journey', href: '/how-it-works', icon: Sparkles, color: '#7B1FA2' },
      { label: 'Pricing', desc: 'Processing fee + success fee', href: '/pricing', icon: Tag, color: '#1B8A5A' },
    ],
    featured: { label: 'Start Rating with FundRaising', href: '/#start', tag: 'Begin', desc: 'Name, email, phone — rating starts inside fundraising' },
  },
  Company: {
    heading: 'Micus',
    items: [
      { label: 'About', desc: 'Why rating + raise belong together', href: '/about', icon: Building2, color: '#C2185B' },
      { label: 'Rating policy', desc: 'How we assess readiness', href: '/credit-rating', icon: Scale, color: '#7B1FA2' },
      { label: 'Raise procedure', desc: 'Steps after your rating', href: '/fundraising', icon: FileText, color: '#1B8A5A' },
    ],
    secondary: {
      heading: 'Company types we rate',
      items: sharedCompanyTypes.map((ct, i) => ({
        label: ct.title,
        desc: ct.subtitle,
        href: `/credit-rating#company-${ct.id}`,
        icon: companyTypeIcons[i % companyTypeIcons.length],
        color: companyTypeColors[i % companyTypeColors.length],
      })),
    },
  },
  Help: {
    heading: 'Support',
    items: [
      { label: 'FAQ', desc: 'Common questions', href: '/faq', icon: HelpCircle, color: '#C2185B' },
      { label: 'Contact', desc: 'Talk to the team', href: '/contact', icon: MessageSquare, color: '#1B8A5A' },
      { label: 'Privacy', desc: 'How we handle data', href: '/privacy', icon: Shield, color: '#7B1FA2' },
      { label: 'Terms', desc: 'Terms of service', href: '/terms', icon: FileText, color: '#FF7043' },
    ],
  },
};

const topNav: { label: string; href: string; hasMega: boolean; isUniversal?: boolean; icon?: React.ElementType }[] = [
  { label: 'Home', href: '/', hasMega: false, icon: Home },
  { label: 'Solutions', href: '/how-it-works', hasMega: true },
  { label: 'Pricing', href: '/pricing', hasMega: false },
  { label: 'Universal', href: '/', hasMega: true, isUniversal: true },
  { label: 'Company', href: '/about', hasMega: true },
  { label: 'Help', href: '/faq', hasMega: true },
];

function MegaItems({ items }: { items: MegaItem[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map(({ label, desc, href, icon: Icon, color }) => (
        <Link
          key={href + label}
          to={href}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, textDecoration: 'none', color: '#1A0A10' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#FFF0F5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, border: `1px solid ${color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={16} style={{ color }} />
          </div>
          <div>
            <p style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>{label}</p>
            <p style={{ fontSize: 11.5, color: '#9B6E7E', margin: '1px 0 0' }}>{desc}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Header() {
  const content = useAppSelector((s) => s.geo.content);
  const countryCode = useAppSelector((s) => s.geo.countryCode);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMega, setActiveMega] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveMega(null);
  }, [location.pathname]);

  const openMega = (label: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMega(label);
  };
  const closeMega = () => {
    closeTimer.current = setTimeout(() => setActiveMega(null), 120);
  };

  const universalPanel = (
    <div style={{ padding: 20 }}>
      <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9B6E7E', marginBottom: 10 }}>
        Supported countries
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {supportedCountries.map((c) => {
          const active = countryCode === c.code;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => switchCountryAndRefresh(c.code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 12,
                border: active ? '1px solid #F0C8D8' : '1px solid transparent',
                background: active ? '#FFF0F5' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = '#FFF0F5';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#C2185B18', border: '1px solid #C2185B25', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                {c.flag}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13.5, fontWeight: 700, margin: 0, color: '#1A0A10' }}>
                  {c.label}{active ? ' · Active' : ''}
                </p>
                <p style={{ fontSize: 11.5, color: '#9B6E7E', margin: '1px 0 0' }}>{c.desc}</p>
              </div>
              <ArrowRight size={14} color="#C2185B" />
            </button>
          );
        })}
        <Link
          to="/universal/not-listed"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 12,
            border: '1px dashed #F0C8D8',
            background: 'transparent',
            textAlign: 'left',
            width: '100%',
            marginTop: 6,
            textDecoration: 'none',
            color: 'inherit',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#7B1FA218', border: '1px solid #7B1FA225', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MapPin size={16} color="#7B1FA2" />
          </div>
          <div>
            <p style={{ fontSize: 13.5, fontWeight: 700, margin: 0, color: '#1A0A10' }}>Not listed</p>
            <p style={{ fontSize: 11.5, color: '#9B6E7E', margin: '1px 0 0' }}>Request Micus for your country</p>
          </div>
        </Link>
      </div>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div
        style={{
          background: 'linear-gradient(90deg, #2D0A1A 0%, #880E4F 50%, #2D0A1A 100%)',
          height: scrolled ? 0 : 34,
          overflow: 'hidden',
          transition: 'height 0.35s cubic-bezier(.4,0,.2,1)',
        }}
      >
        <div className="container-wide h-full flex items-center justify-between" style={{ gap: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {content.topbarTagline}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
            <a href={content.phoneHref} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,0.8)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <Phone size={10} /> {content.phone}
            </a>
            <a href={`mailto:${content.email}`} className="topbar-email" style={{ display: 'none', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,0.8)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <Mail size={10} /> {content.email}
            </a>
          </div>
        </div>
      </div>

      <div
        style={{
          background: scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,251,248,0.96)',
          backdropFilter: 'blur(16px)',
          boxShadow: scrolled ? '0 2px 32px rgba(194,24,91,0.10)' : '0 1px 0 rgba(240,200,216,0.5)',
          transition: 'all 0.3s ease',
        }}
      >
        <div className="container-wide" style={{ display: 'flex', alignItems: 'center', height: 68 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, textDecoration: 'none', marginRight: 32 }}>
            <BrandLogo size={40} />
            <span style={{ fontSize: 26, fontWeight: 800, color: '#8E0E3F', letterSpacing: '-0.02em' }}>micus</span>
          </Link>

          <nav style={{ display: 'none', flex: 1, alignItems: 'center', gap: 2 }} className="lg-nav">
            {topNav.map((link) => {
              if (!link.hasMega) {
                const Icon = link.icon;
                return (
                  <NavLink
                    key={link.href}
                    to={link.href}
                    end={link.href === '/'}
                    style={({ isActive }) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 14px',
                      fontSize: 13.5,
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? '#C2185B' : '#1A0A10',
                      background: isActive ? '#FFF0F5' : 'transparent',
                      borderRadius: 9999,
                      textDecoration: 'none',
                    })}
                  >
                    {Icon && <Icon size={13} />} {link.label}
                  </NavLink>
                );
              }

              const isUniversal = Boolean(link.isUniversal);
              const isActive = activeMega === link.label;
              const menu = megaMenus[link.label];
              const wide = Boolean(menu?.secondary);
              return (
                <div key={link.label} style={{ position: 'relative' }} onMouseEnter={() => openMega(link.label)} onMouseLeave={closeMega}>
                  <button
                    type="button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '7px 14px',
                      fontSize: 13.5,
                      fontWeight: isActive || (isUniversal && countryCode !== 'DEFAULT') ? 700 : 500,
                      color: isActive || isUniversal ? '#C2185B' : '#1A0A10',
                      background: isActive ? '#FFF0F5' : 'transparent',
                      borderRadius: 9999,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {isUniversal && <Globe2 size={13} />}
                    {link.label}
                    <motion.div animate={{ rotate: isActive ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown size={12} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.97 }}
                        transition={{ duration: 0.18 }}
                        onMouseEnter={() => openMega(link.label)}
                        onMouseLeave={closeMega}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 12px)',
                          left: wide ? 0 : '50%',
                          transform: wide ? undefined : 'translateX(-50%)',
                          minWidth: isUniversal ? 320 : wide ? 640 : 360,
                          maxWidth: 'min(720px, calc(100vw - 48px))',
                          background: 'white',
                          borderRadius: 20,
                          boxShadow: '0 16px 60px rgba(26,10,16,0.14), 0 0 0 1px rgba(240,200,216,0.5)',
                          overflow: 'hidden',
                          zIndex: 200,
                        }}
                      >
                        {isUniversal ? (
                          universalPanel
                        ) : menu ? (
                          <>
                            <div style={{ padding: 20, display: wide ? 'grid' : 'block', gridTemplateColumns: wide ? '0.9fr 1.1fr' : undefined, gap: wide ? 8 : undefined }}>
                              <div>
                                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9B6E7E', marginBottom: 10 }}>
                                  {menu.heading}
                                </p>
                                <MegaItems items={menu.items} />
                              </div>
                              {menu.secondary && (
                                <div style={{ borderLeft: wide ? '1px solid #F0C8D8' : undefined, paddingLeft: wide ? 16 : 0, maxHeight: 360, overflowY: 'auto' }}>
                                  <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9B6E7E', marginBottom: 10 }}>
                                    {menu.secondary.heading}
                                  </p>
                                  <MegaItems items={menu.secondary.items} />
                                </div>
                              )}
                            </div>
                            {menu.featured && (
                              <Link
                                to={menu.featured.href}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', background: 'linear-gradient(135deg, #C2185B, #E91E63)', textDecoration: 'none' }}
                              >
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 9999, color: 'white' }}>
                                      {menu.featured.tag}
                                    </span>
                                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'white' }}>{menu.featured.label}</span>
                                  </div>
                                  <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)', margin: 0 }}>{menu.featured.desc}</p>
                                </div>
                                <ArrowRight size={14} color="rgba(255,255,255,0.8)" />
                              </Link>
                            )}
                          </>
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <button
              type="button"
              className="nav-cta-desktop"
              onClick={() => openMega('Universal')}
              style={{ fontSize: 11, fontWeight: 700, color: '#C2185B', background: '#FFF0F5', border: '1px solid #F0C8D8', borderRadius: 999, padding: '6px 12px', cursor: 'pointer', display: 'none', alignItems: 'center', gap: 6 }}
            >
              <Globe2 size={12} /> {content.countryLabel}
            </button>
            <Link
              to="/#start"
              className="nav-cta-desktop"
              style={{
                display: 'none',
                alignItems: 'center',
                padding: '9px 22px',
                fontSize: 13.5,
                fontWeight: 700,
                color: 'white',
                borderRadius: 9999,
                background: 'linear-gradient(135deg, #C2185B, #E91E63)',
                boxShadow: '0 4px 16px rgba(233,30,99,0.30)',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Rating & raise
            </Link>
            <button type="button" onClick={() => setMobileOpen(true)} className="nav-burger" style={{ padding: 8, color: '#1A0A10', background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Open menu">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 40 }} onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 320, background: 'white', zIndex: 50, display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #F0C8D8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <BrandLogo size={32} />
                  <span style={{ fontSize: 20, fontWeight: 800, color: '#C2185B' }}>micus</span>
                </div>
                <button type="button" onClick={() => setMobileOpen(false)} style={{ padding: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#6B3A52' }}>
                  <X size={20} />
                </button>
              </div>
              <nav style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, color: '#1A0A10', fontSize: 14, fontWeight: 500 }}>
                  <Home size={16} color="#C2185B" /> Home
                </Link>
                <Link to="/pricing" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, color: '#1A0A10', fontSize: 14, fontWeight: 500 }}>
                  <Tag size={16} color="#C2185B" /> Pricing
                </Link>

                <div>
                  <button
                    type="button"
                    onClick={() => setMobileExpanded(mobileExpanded === 'Universal' ? null : 'Universal')}
                    style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, border: 'none', background: mobileExpanded === 'Universal' ? '#FFF0F5' : 'transparent', color: mobileExpanded === 'Universal' ? '#C2185B' : '#1A0A10', fontSize: 14, fontWeight: mobileExpanded === 'Universal' ? 700 : 500, cursor: 'pointer' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Globe2 size={15} /> Universal</span>
                    <ChevronDown size={14} />
                  </button>
                  {mobileExpanded === 'Universal' && (
                    <div style={{ padding: '4px 8px 12px' }}>
                      {supportedCountries.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => switchCountryAndRefresh(c.code)}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px 10px 20px', border: 'none', background: countryCode === c.code ? '#FFF0F5' : 'transparent', color: '#6B3A52', fontSize: 13.5, cursor: 'pointer', textAlign: 'left', borderRadius: 10, fontFamily: 'inherit' }}
                        >
                          <span>{c.flag}</span> {c.label}
                        </button>
                      ))}
                      <Link
                        to="/universal/not-listed"
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px 10px 20px', color: '#6B3A52', fontSize: 13.5, textDecoration: 'none' }}
                      >
                        <MapPin size={14} color="#7B1FA2" /> Not listed
                      </Link>
                    </div>
                  )}
                </div>

                {Object.entries(megaMenus).map(([label, menu]) => (
                  <div key={label}>
                    <button
                      type="button"
                      onClick={() => setMobileExpanded(mobileExpanded === label ? null : label)}
                      style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, border: 'none', background: mobileExpanded === label ? '#FFF0F5' : 'transparent', color: mobileExpanded === label ? '#C2185B' : '#1A0A10', fontSize: 14, fontWeight: mobileExpanded === label ? 700 : 500, cursor: 'pointer' }}
                    >
                      {label}
                      <ChevronDown size={14} />
                    </button>
                    {mobileExpanded === label && (
                      <>
                        {menu.items.map((item) => (
                          <Link key={item.href + item.label} to={item.href} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px 10px 28px', color: '#6B3A52', fontSize: 13.5 }}>
                            <item.icon size={14} style={{ color: item.color }} /> {item.label}
                          </Link>
                        ))}
                        {menu.secondary?.items.map((item) => (
                          <Link key={item.href + item.label} to={item.href} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px 10px 28px', color: '#6B3A52', fontSize: 13.5 }}>
                            <item.icon size={14} style={{ color: item.color }} /> {item.label}
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                ))}
              </nav>
              <div style={{ padding: 16, borderTop: '1px solid #F0C8D8' }}>
                <Link to="/#start" style={{ display: 'flex', justifyContent: 'center', padding: 14, fontWeight: 700, color: 'white', background: 'linear-gradient(135deg,#C2185B,#E91E63)', borderRadius: 999, textDecoration: 'none' }}>
                  Rating & raise
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        @media (min-width: 640px) { .topbar-email { display: flex !important; } }
        @media (min-width: 768px) { .nav-cta-desktop { display: inline-flex !important; } }
        @media (min-width: 1024px) { .lg-nav { display: flex !important; } .nav-burger { display: none !important; } }
      `}</style>
    </header>
  );
}
